import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, RefreshCw, MessageSquare, HelpCircle, ArrowRight,
  TrendingUp, Coins, Briefcase, Target, Award, GraduationCap, Zap, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Widget } from "@/components/dashboard/Widget";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const ICON_MAP = {
  "trending-up": TrendingUp,
  coins: Coins,
  briefcase: Briefcase,
  target: Target,
  award: Award,
  "graduation-cap": GraduationCap,
  sparkles: Sparkles,
  zap: Zap,
};

/** AI Command Center — the daily executive briefing. */
export const CommandCenterWidget = () => {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [refreshing, setRefreshing] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [whyState, setWhyState] = useState({ loading: false, data: null, error: false });

  const load = async () => {
    setState({ loading: true, error: false, data: null });
    try {
      const { data } = await api.get("/dashboard/briefing");
      setState({ loading: false, error: false, data });
    } catch {
      setState({ loading: false, error: true, data: null });
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/dashboard/briefing/refresh");
      setState({ loading: false, error: false, data });
      toast.success("Briefing refreshed");
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("Daily refresh limit reached — try again tomorrow");
      } else {
        toast.error("Couldn't refresh briefing");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const openWhy = async () => {
    setWhyOpen(true);
    setWhyState({ loading: true, data: null, error: false });
    try {
      const { data } = await api.post("/dashboard/briefing/why");
      setWhyState({ loading: false, data: data.why, error: false });
    } catch {
      setWhyState({ loading: false, data: null, error: true });
    }
  };

  const briefing = state.data?.briefing;
  const topAction = briefing?.top_action;

  return (
    <Widget
      size="full"
      className="relative overflow-hidden bg-gradient-to-br from-primary/[0.08] via-card to-card border-primary/20"
      padding="p-6 sm:p-7"
      testId="command-center-widget"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-primary/10 blur-3xl rounded-full" />

      <div className="relative flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-primary flex items-center gap-1.5 mb-1">
            <Sparkles size={11} strokeWidth={2} /> AI Command Center · Daily briefing
          </div>
          <h2
            className="font-display font-black text-2xl sm:text-3xl leading-tight tracking-tight text-foreground"
            data-testid="command-center-greeting"
          >
            {state.loading
              ? <span className="skeleton inline-block h-7 w-64 align-middle rounded-md" />
              : (briefing?.greeting || "Your briefing is loading…")}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={refreshing}
            className="gap-1.5"
            data-testid="command-center-refresh"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} strokeWidth={1.75} />
            <span className="hidden sm:inline">{refreshing ? "Refreshing…" : "Refresh"}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => nav("/app/chat")}
            className="gap-1.5"
            data-testid="command-center-ask-ai"
          >
            <MessageSquare size={13} strokeWidth={1.75} />
            <span className="hidden sm:inline">Ask AI</span>
          </Button>
        </div>
      </div>

      {state.loading && (
        <div className="space-y-3">
          <div className="skeleton h-5 w-full" />
          <div className="skeleton h-5 w-11/12" />
          <div className="skeleton h-5 w-9/12" />
          <div className="skeleton h-24 w-full mt-4" />
        </div>
      )}

      {state.error && !state.loading && (
        <div className="text-sm text-muted-foreground py-3">
          Couldn&apos;t load today&apos;s briefing.{" "}
          <button onClick={load} className="text-primary hover:underline font-medium">Retry</button>
        </div>
      )}

      {!state.loading && !state.error && briefing && (
        <>
          <p className="text-base sm:text-lg text-foreground/90 leading-relaxed mb-5 max-w-4xl" data-testid="command-center-narrative">
            {briefing.narrative}
          </p>

          {Array.isArray(briefing.highlights) && briefing.highlights.length > 0 && (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-6" data-testid="command-center-highlights">
              {briefing.highlights.slice(0, 6).map((h, i) => {
                const Icon = ICON_MAP[h.icon] || Sparkles;
                return (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-2.5 text-sm text-foreground/90"
                  >
                    <span className="w-6 h-6 rounded-md bg-primary/10 text-primary flex-shrink-0 flex items-center justify-center mt-0.5">
                      <Icon size={12} strokeWidth={2} />
                    </span>
                    <span className="leading-snug">{h.text}</span>
                  </motion.li>
                );
              })}
            </ul>
          )}

          {topAction && (
            <div
              className="relative rounded-xl border border-primary/30 bg-primary/[0.06] p-4 sm:p-5"
              data-testid="command-center-top-action"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1.5">
                Today&apos;s highest-ROI action
              </div>
              <div className="font-display font-bold text-lg sm:text-xl text-foreground leading-snug mb-1.5">
                {topAction.title}
              </div>
              {topAction.why && (
                <div className="text-sm text-muted-foreground mb-2 max-w-3xl leading-relaxed">
                  {topAction.why}
                </div>
              )}
              {topAction.roi_label && (
                <div className="text-xs font-mono font-semibold text-[hsl(var(--success))] mb-3">
                  {topAction.roi_label}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {topAction.cta_path && (
                  <Button
                    size="sm"
                    onClick={() => nav(topAction.cta_path)}
                    className="gap-1.5"
                    data-testid="command-center-take-action"
                  >
                    Take action <ArrowRight size={13} strokeWidth={2} />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openWhy}
                  className="gap-1.5"
                  data-testid="command-center-why"
                >
                  <HelpCircle size={13} strokeWidth={1.75} /> Why?
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={whyOpen} onOpenChange={setWhyOpen}>
        <DialogContent className="max-w-lg" data-testid="command-center-why-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle size={16} className="text-primary" /> Why this action?
            </DialogTitle>
            <DialogDescription>
              The reasoning your AI Career Advisor used to pick today&apos;s top recommendation.
            </DialogDescription>
          </DialogHeader>

          {whyState.loading && (
            <div className="space-y-2 py-3">
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-11/12" />
              <div className="skeleton h-4 w-9/12" />
            </div>
          )}
          {whyState.error && (
            <div className="text-sm text-muted-foreground py-3">
              Couldn&apos;t load the rationale. Please try again.
            </div>
          )}
          {whyState.data && (
            <div className="space-y-4 text-sm">
              <p className="text-foreground/90 leading-relaxed" data-testid="why-rationale">
                {whyState.data.rationale}
              </p>
              {Array.isArray(whyState.data.evidence) && whyState.data.evidence.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    Evidence
                  </div>
                  <ul className="space-y-1.5">
                    {whyState.data.evidence.map((e, i) => (
                      <li key={i} className="flex items-center justify-between text-xs bg-secondary/40 rounded-md px-2.5 py-1.5">
                        <span className="text-foreground/80">{e.metric}</span>
                        <span className="flex items-center gap-2 font-mono">
                          <span className="font-semibold text-foreground">{e.value}</span>
                          {e.trend && <span className="text-[hsl(var(--success))] text-[10px]">{e.trend}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {whyState.data.expected_impact && (
                <div className="rounded-md bg-primary/[0.08] border border-primary/20 p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                    Expected impact
                  </div>
                  <div className="text-xs text-foreground/90 space-y-0.5">
                    {whyState.data.expected_impact.monetary && (
                      <div><span className="text-muted-foreground">Monetary:</span> {whyState.data.expected_impact.monetary}</div>
                    )}
                    {whyState.data.expected_impact.timeframe && (
                      <div><span className="text-muted-foreground">Timeframe:</span> {whyState.data.expected_impact.timeframe}</div>
                    )}
                    {whyState.data.expected_impact.confidence_pct != null && (
                      <div><span className="text-muted-foreground">Confidence:</span> {whyState.data.expected_impact.confidence_pct}%</div>
                    )}
                  </div>
                </div>
              )}
              {Array.isArray(whyState.data.risks) && whyState.data.risks.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                    Risks & caveats
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {whyState.data.risks.map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <ChevronRight size={11} className="mt-0.5 flex-shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setWhyOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Widget>
  );
};
